import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  User,
  BarChart3,
  Eye,
  Shield,
  FileText,
  TrendingUp,
  Target,
  Loader2,
  ChevronDown,
  ChevronUp,
  Award,
  ExternalLink,
  Image as ImageIcon,
} from "lucide-react";
import { format } from "date-fns";

// Compact Question Accordion Component
const QuestionAccordion = ({ questions }) => {
  const [expandedItems, setExpandedItems] = useState(new Set());

  const toggleItem = (index) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  const formatTime = (seconds) => {
    if (seconds === 0) return "0m 0s";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "easy":
        return "bg-green-100 text-green-700 border-green-300";
      case "medium":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "hard":
        return "bg-red-100 text-red-700 border-red-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  // Helper function to normalize answer to array
  const normalizeAnswer = (answer) => {
    if (!answer) return [];
    if (Array.isArray(answer)) return answer;
    return [answer];
  };

  const getChoiceStyle = (choice, question, index) => {
    const userAnswers = normalizeAnswer(question.userAnswer);
    const correctAnswers = normalizeAnswer(question.correctAnswer);
    
    const isUserAnswer = userAnswers.includes(choice);
    const isCorrectAnswer = correctAnswers.includes(choice);

    if (isCorrectAnswer && isUserAnswer) {
      return "bg-green-50 border-green-300 text-green-800";
    } else if (isCorrectAnswer) {
      return "bg-green-50 border-green-200 text-green-700";
    } else if (isUserAnswer) {
      return "bg-red-50 border-red-300 text-red-800";
    }
    return "bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100";
  };

  const getChoiceIcon = (choice, question) => {
    const userAnswers = normalizeAnswer(question.userAnswer);
    const correctAnswers = normalizeAnswer(question.correctAnswer);
    
    const isUserAnswer = userAnswers.includes(choice);
    const isCorrectAnswer = correctAnswers.includes(choice);

    if (isCorrectAnswer && isUserAnswer) {
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    } else if (isCorrectAnswer) {
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    } else if (isUserAnswer) {
      return <XCircle className="h-4 w-4 text-red-500" />;
    }
    return null;
  };

  if (!questions || questions.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No questions available for analysis
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {questions.map((question, index) => {
        const isExpanded = expandedItems.has(index);
        const questionNumber = index + 1;

        return (
          <div
            key={question.id || index}
            className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
          >
            {/* Compact Accordion Header */}
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset transition-colors duration-200"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 flex-1 min-w-0">
                  {/* Question Number Badge */}
                  <div className="flex-shrink-0 w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-semibold text-blue-800">
                      Q{questionNumber}
                    </span>
                  </div>

                  {/* Question Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-sm font-medium text-gray-900 truncate max-w-md">
                        {question.question}
                      </h3>
                      {question.difficultyLevel && (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getDifficultyColor(
                            question.difficultyLevel
                          )}`}
                        >
                          {question.difficultyLevel}
                        </span>
                      )}

                      {question.type && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                          {question.type}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center space-x-3 mt-1">
                      {question.category && (
                        <span className="text-xs text-gray-500">
                          {question.category}
                        </span>
                      )}
                      <div className="flex items-center space-x-1">
                        <Clock className="h-3 w-3 text-gray-400" />
                        <span className="text-xs text-gray-500">
                          {formatTime(question.timeSpent || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Right Side Info */}
                <div className="flex items-center space-x-3 flex-shrink-0">
                  <div className="flex items-center space-x-1">
                    <Award className="h-3 w-3 text-gray-400" />
                    <span className="text-xs text-gray-600">
                      {question.marks || 0}/{question.value || 1}
                    </span>
                  </div>

                  {question.isCorrect ? (
                    <div className="flex items-center space-x-1 bg-green-100 px-2 py-0.5 rounded-full">
                      <CheckCircle className="h-3 w-3 text-green-600" />
                      <span className="text-xs font-medium text-green-800">
                        Correct
                      </span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1 bg-red-100 px-2 py-0.5 rounded-full">
                      <XCircle className="h-3 w-3 text-red-600" />
                      <span className="text-xs font-medium text-red-800">
                        Wrong
                      </span>
                    </div>
                  )}

                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </div>
              </div>
            </button>

            {/* Compact Accordion Content */}
            {isExpanded && (
              <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                <div className="pt-3 space-y-3">
                  {/* Question Details */}

                  {/* Answer Choices - More Compact */}
                  {question.choices && question.choices.length > 0 && (
                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2 text-sm">
                        Answer Choices
                      </h4>
                      <div className="grid grid-cols-1 gap-1.5">
                        {question.choices.map((choice, choiceIndex) => {
                          const choiceLabel = String.fromCharCode(
                            65 + choiceIndex
                          );
                          return (
                            <div
                              key={choiceIndex}
                              className={`flex items-center space-x-2 p-2 rounded border transition-colors text-sm ${getChoiceStyle(
                                choice,
                                question,
                                choiceIndex
                              )}`}
                            >
                              <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white border border-gray-300 flex items-center justify-center">
                                <span className="text-xs font-medium text-gray-600">
                                  {choiceLabel}
                                </span>
                              </div>
                              <span className="flex-1">{choice}</span>
                              {getChoiceIcon(choice, question)}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Compact Answer Summary */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2 flex items-center text-sm">
                        <CheckCircle className="h-3 w-3 mr-1.5 text-green-500" />
                        Correct Answer
                      </h4>
                      <div className="space-y-1">
                        {(() => {
                          const correctAnswers = normalizeAnswer(question.correctAnswer);
                          return correctAnswers.length > 0 ? (
                            correctAnswers.map((answer, idx) => (
                              <span
                                key={idx}
                                className="inline-block bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-medium mr-1"
                              >
                                {answer}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500 text-xs italic">
                              No correct answer provided
                            </span>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="bg-white rounded-md p-3 border border-gray-200">
                      <h4 className="font-medium text-gray-900 mb-2 text-sm">
                        Your Answer
                      </h4>
                      <div className="space-y-1">
                        {(() => {
                          const userAnswers = normalizeAnswer(question.userAnswer);
                          const correctAnswers = normalizeAnswer(question.correctAnswer);
                          return userAnswers.length > 0 ? (
                            userAnswers.map((answer, idx) => (
                              <span
                                key={idx}
                                className={`inline-block px-2 py-0.5 rounded text-xs font-medium mr-1 ${
                                  correctAnswers.includes(answer)
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {answer}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-500 text-xs italic">
                              Not answered
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ExamDetailView = ({
  sessionData,
  onExportPDF,
  isLoading,
  isDownloading,
}) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!sessionData) {
    return (
      <div className="text-center p-6 text-gray-500">No exam details found</div>
    );
  }

  const {
    session,
    candidate,
    exam,
    result,
    questions,
    proctoringData,
    performanceMetrics,
  } = sessionData;

  const getGradeColor = (grade) => {
    switch (grade) {
      case "A":
        return "bg-green-100 text-green-800 border-green-200";
      case "B":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "C":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "D":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "F":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case "High":
        return "bg-red-100 text-red-800 border-red-200";
      case "Medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "Low":
        return "bg-green-100 text-green-800 border-green-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">
            {exam?.name} - Detailed Analysis
          </h2>
          <p className="text-muted-foreground mt-1">
            Comprehensive exam performance and proctoring analysis
          </p>
        </div>
        <Button
          onClick={onExportPDF}
          className="flex items-center gap-2"
          disabled={isDownloading}
        >
          {isDownloading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )}
          Export PDF
        </Button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Overall Score
                </p>
                <p className="text-3xl font-bold text-primary">
                  {result?.percentage || 0}%
                </p>
              </div>
              <Target className="h-8 w-8 text-primary" />
            </div>
            {result && (
              <Badge className={`mt-2 ${getGradeColor(result.grade)}`}>
                Grade {result.grade}
              </Badge>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Questions
                </p>
                <p className="text-3xl font-bold">
                  {result?.correctAnswers || 0}/{result?.totalQuestions || 0}
                </p>
              </div>
              <FileText className="h-8 w-8 text-blue-500" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {performanceMetrics?.accuracy || 0}% Accuracy
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Time Used
                </p>
                <p className="text-3xl font-bold">{session?.duration || 0}m</p>
              </div>
              <Clock className="h-8 w-8 text-orange-500" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {session?.timeUtilization || 0}% of allotted time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">
                  Risk Level
                </p>
                <Badge className={getRiskColor(proctoringData?.riskLevel)}>
                  {proctoringData?.riskLevel || "Low"}
                </Badge>
              </div>
              <Shield className="h-8 w-8 text-purple-500" />
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              {proctoringData?.totalViolations || 0} violations
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Tabs */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="questions">Questions</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="proctoring">Proctoring</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Candidate Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Name:</span>
                  <span className="font-medium">{candidate?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span className="font-medium">{candidate?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Mobile:</span>
                  <span className="font-medium">
                    {candidate?.mobile || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Start Time:</span>
                  <span className="font-medium">
                    {session?.startTime
                      ? format(new Date(session.startTime), "PPp")
                      : "N/A"}
                  </span>
                </div>
                {session?.endTime && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">End Time:</span>
                    <span className="font-medium">
                      {format(new Date(session.endTime), "PPp")}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Exam Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Exam Name:</span>
                  <span className="font-medium">{exam?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Total Questions:
                  </span>
                  <span className="font-medium">{exam?.totalQuestions}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Time Allotted:</span>
                  <span className="font-medium">{exam?.totalTime} minutes</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Passing Score:</span>
                  <span className="font-medium">{exam?.passingScore}%</span>
                </div>
                {result && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Result:</span>
                    <Badge
                      className={
                        result.status === "Passed"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }
                    >
                      {result.status}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="questions">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Question Analysis</CardTitle>
              <p className="text-sm text-muted-foreground">
                Click on any question to view detailed analysis with choices and
                answers
              </p>
            </CardHeader>
            <CardContent>
              <QuestionAccordion questions={questions} />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Accuracy</span>
                      <span className="text-sm text-muted-foreground">
                        {performanceMetrics?.accuracy || 0}%
                      </span>
                    </div>
                    <Progress
                      value={performanceMetrics?.accuracy || 0}
                      className="h-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">Speed (Q/hr)</span>
                      <span className="text-sm text-muted-foreground">
                        {performanceMetrics?.speed || 0}
                      </span>
                    </div>
                    <Progress
                      value={Math.min(
                        (performanceMetrics?.speed || 0) * 2,
                        100
                      )}
                      className="h-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium">
                        Time Utilization
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {session?.timeUtilization || 0}%
                      </span>
                    </div>
                    <Progress
                      value={session?.timeUtilization || 0}
                      className="h-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {performanceMetrics?.categoryBreakdown && (
              <Card>
                <CardHeader>
                  <CardTitle>Category-wise Performance</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(performanceMetrics.categoryBreakdown).map(
                      ([category, stats]) => (
                        <div key={category} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium">
                              {category}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {stats.correctAnswers}/{stats.questionsAnswered} (
                              {stats.accuracy}%)
                            </span>
                          </div>
                          <Progress value={stats.accuracy} className="h-2" />
                        </div>
                      )
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="proctoring">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-500">
                      {proctoringData?.warnings?.length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Warnings</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-500">
                      {proctoringData?.tabSwitches?.length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Tab Switches
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-yellow-500">
                      {proctoringData?.focusLosses?.length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Focus Losses
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-500">
                      {proctoringData?.suspiciousActivities?.length || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">Suspicious</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Screenshots Section */}
            {sessionData?.screenshots && sessionData.screenshots.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5 text-blue-500" />
                    Captured Screenshots ({sessionData.screenshots.length})
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Screenshots captured during the exam for proctoring purposes
                  </p>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {sessionData.screenshots.map((screenshot, index) => (
                      <div
                        key={index}
                        className="border rounded-lg overflow-hidden bg-gray-50"
                      >
                        <div className="aspect-video relative">
                          <img
                            src={screenshot}
                            alt={`Screenshot ${index + 1}`}
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(screenshot, '_blank')}
                            onError={(e) => {
                              e.target.src = '/placeholder-image.png';
                              e.target.alt = 'Failed to load screenshot';
                            }}
                          />
                        </div>
                        <div className="p-3">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-700">
                              Screenshot {index + 1}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(screenshot, '_blank')}
                              className="h-7 px-2 text-xs"
                            >
                              <ExternalLink className="h-3 w-3 mr-1" />
                              View
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {proctoringData?.warnings && proctoringData.warnings.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Warning Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {proctoringData.warnings.map((warning, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              {warning.timestamp
                                ? format(
                                    new Date(warning.timestamp),
                                    "HH:mm:ss"
                                  )
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Badge className="bg-red-100 text-red-800">
                                {warning.type || "Warning"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {warning.details || "No details provided"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExamDetailView;
